import { UseQueryResult, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useAuthenticated } from '../auth';
import { useBoolFlag } from '../components/flags';
import { TRACK_FAULTS_UI_TEMPLATE_ENABLEMENT_FLAG } from '../feature-flags';
import { GetThingFaultSummaryInput, GetThingFaultSummaryResult } from '../types/thing-fault-summary';
import { getThingFaultSummary } from '../util/thing-fault-summary';

export interface UseThingFaultSummaryQueryOptions {
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number;
  retry?: boolean | number;
}

export const getThingFaultSummaryQueryKey = (input: GetThingFaultSummaryInput): (string | Record<string, unknown>)[] => {
  return [
    'thing-fault-summary',
    {
      thingId: input.thingId,
      range: input.range ?? 30,
      status: input.status,
      warning: input.warning,
    },
  ];
};

export const useThingFaultSummaryQueryKey = getThingFaultSummaryQueryKey;

export const useThingFaultSummaryQuery = (
  input: GetThingFaultSummaryInput,
  options?: UseThingFaultSummaryQueryOptions,
): UseQueryResult<GetThingFaultSummaryResult> => {
  const { restClient } = useAuthenticated();
  const faultsUiTemplateEnabled = useBoolFlag(TRACK_FAULTS_UI_TEMPLATE_ENABLEMENT_FLAG) ?? false;

  const queryKey = getThingFaultSummaryQueryKey(input);

  const queryFn = useCallback(
    () => getThingFaultSummary(restClient, input.thingId, input.range, input.status, input.warning),
    [input, restClient],
  );

  return useQuery({
    queryKey,
    queryFn,
    enabled: (options?.enabled ?? true) && faultsUiTemplateEnabled && Boolean(input.thingId),
    staleTime: options?.staleTime ?? 1000 * 60 * 60,
    gcTime: options?.gcTime ?? 1000 * 60 * 60 * 24,
    retry: options?.retry ?? true,
  });
};

export const useInvalidateThingFaultSummary = (): ((input: Partial<GetThingFaultSummaryInput>) => Promise<void>) => {
  const queryClient = useQueryClient();

  return async (input: Partial<GetThingFaultSummaryInput>) => {
    const queryKey = getThingFaultSummaryQueryKey({
      thingId: input.thingId ?? '',
      range: input.range,
      status: input.status,
      warning: input.warning,
    });

    await queryClient.invalidateQueries({
      queryKey,
    });
  };
};
